import { useState } from 'react';
import { useSelector } from 'react-redux';
import { axios } from 'services/axios';
import Cookies from 'js-cookie';
import {
    FIELDS_TO_EXPORT,
    FIELDS_WITH_ERRORS,
    TEMPLATE_FILENAME,
    CSV_TYPE,
    CSV_TYPE_ERROR,
    BLOCK_DNE_ERROR,
    CANNOT_DELETE_WITH_COMPANY_ERROR,
    NO_CHANGES_ERROR,
    READ_ONLY_FIELD_MODIFIED_ERROR,
    DEFAULT_VERIFICATION_RESULT,
    VERIFICATION_CHECKS,
    INVALID_FILENAME,
} from './constants';
import Papa from 'papaparse';
import keyBy from 'lodash/keyBy';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import delay from 'lodash/delay';

import { downloadCSV } from 'utils/utils';

export default function useCSVNumberBlocksUpdate() {
    const { currentWholesaler, currentPartner } = useSelector(
        (state) => state.navigationLists,
    );
    const { currentScope } = useSelector((state) => state.login);
    const [uploadData, setUploadData] = useState(null);
    const [error, setError] = useState(null);
    const [templateLoading, setTemplateLoading] = useState(false);
    const [exportType, setExportType] = useState(null); // 'company' or 'unassigned'
    const [selectedCompanyForExport, setSelectedCompanyForExport] =
        useState(null);
    // eslint-disable-next-line no-unused-vars
    const [lookup, setLookup] = useState({
        numberBlocksLookup: {},
    });

    const [payloads, setPayloads] = useState(null);
    const [verificationChecks, setVerificationChecks] =
        useState(VERIFICATION_CHECKS);
    const [verificationResult, setVerificationResult] = useState(
        DEFAULT_VERIFICATION_RESULT,
    );

    const changeVerificationChecks = (i) => {
        delay(
            (index) => {
                setVerificationChecks((prev) =>
                    prev.map((check, idx) => {
                        if (idx === index) return { ...check, check: true };
                        if (idx === index + 1)
                            return { ...check, current: true };
                        return check;
                    }),
                );
            },
            750,
            i,
        );
    };

    const changeVerificationProgresses = (i) => {
        delay(
            (index) => {
                setVerificationChecks((prev) =>
                    prev.map((check, idx) =>
                        idx === index
                            ? {
                                  ...check,
                                  completionCounter:
                                      check.completionCounter + 1,
                              }
                            : check,
                    ),
                );
            },
            750,
            i,
        );
    };

    const handleExportInvalidData = () => {
        const exportInvalidData = verificationResult.failed.data.map(
            (block) => {
                return Object.keys(FIELDS_WITH_ERRORS).reduce((acc, key) => {
                    const value = block[key];
                    const newKey = FIELDS_WITH_ERRORS[key];
                    acc[newKey] = value;
                    return acc;
                }, {});
            },
        );
        const res = Papa.unparse(exportInvalidData);
        downloadCSV(res, INVALID_FILENAME);
    };

    const sanitizeCSVField = (value) => {
        if (typeof value !== 'string' || value.length === 0) return value;
        const firstChar = value.charAt(0);
        if (
            firstChar === '=' ||
            firstChar === '+' ||
            firstChar === '-' ||
            firstChar === '@' ||
            firstChar === '\t' ||
            firstChar === '\r' ||
            firstChar === '\n'
        ) {
            return "'" + value;
        }
        return value;
    };

    const transformJSONData = (data) => {
        return data.map((block) => {
            return Object.keys(FIELDS_TO_EXPORT).reduce((acc, key) => {
                let value = block[key];
                const newKey = FIELDS_TO_EXPORT[key];

                if (value === undefined || value === null) {
                    value = '';
                }

                // Always set Delete to FALSE
                if (key === 'delete') {
                    value = 'FALSE';
                }

                // Convert boolean to TRUE/FALSE string
                if (key === 'publicPool' && typeof value === 'boolean') {
                    value = value ? 'TRUE' : 'FALSE';
                }

                value = sanitizeCSVField(value);

                acc[newKey] = value;
                return acc;
            }, {});
        });
    };

    const handleDownloadCSVTemplate = async (
        type,
        companyID = null,
        companyName = null,
    ) => {
        try {
            setTemplateLoading(true);
            setExportType(type);
            setSelectedCompanyForExport(companyID);

            let blocks = [];

            if (type === 'company' && companyID) {
                // For company-specific export, use Company Numbers endpoint
                const response = await axios.get(
                    `/Company/${companyID}/Numbers`,
                );
                const numbers = response.data.numbers || [];

                // Get unique block IDs from numbers
                const blockIDs = [
                    ...new Set(
                        numbers.map((n) => n.numberBlockID).filter(Boolean),
                    ),
                ];

                if (blockIDs.length === 0) {
                    setError('No number blocks found for this company.');
                    setTemplateLoading(false);
                    return;
                }

                // Get all blocks from the list endpoint
                let url;
                if (currentScope === 40) {
                    url = `/partner/${currentPartner}/numberblocks/list`;
                } else if (currentScope === 60) {
                    url = `/numberblocks/list/${currentWholesaler}`;
                } else if (currentScope === 80) {
                    url = '/numberblocks/list';
                }

                const blocksResponse = await axios.get(url);
                const allBlocks = blocksResponse.data || [];

                // Filter to blocks that have numbers in this company
                // Set companyID since blocks are being exported for this company
                blocks = allBlocks
                    .filter((block) => blockIDs.includes(block.id))
                    .map((block) => ({
                        ...block,
                        partnerID:
                            currentScope === 40
                                ? currentPartner
                                : block.partnerID || '',
                        companyID: companyID, // Set company ID for these blocks
                        companyName: companyName || '',
                    }));
            } else if (type === 'unassigned') {
                // Get all blocks using the appropriate list endpoint
                let url;
                if (currentScope === 40) {
                    url = `/partner/${currentPartner}/numberblocks/list`;
                } else if (currentScope === 60) {
                    url = `/numberblocks/list/${currentWholesaler}`;
                } else if (currentScope === 80) {
                    url = '/numberblocks/list';
                }

                const response = await axios.get(url);
                const allBlocks = response.data || [];

                // Filter for unassigned blocks only
                blocks = allBlocks
                    .filter(
                        (block) =>
                            !block.companyName ||
                            block.companyName === null ||
                            block.companyName === '',
                    )
                    .map((block) => ({
                        ...block, // Keep all original block fields
                        companyID: '',
                        companyName: '',
                    }));
            }

            if (blocks.length === 0) {
                setError('No number blocks found matching your selection.');
                setTemplateLoading(false);
                return;
            }

            // Show actual database values in CSV for proper validation
            const transformedData = transformJSONData(blocks);
            const csv = Papa.unparse(transformedData);
            downloadCSV(csv, TEMPLATE_FILENAME);

            // Store download context in cookies for validation
            Cookies.set('numberBlocksExportType', type, { expires: 7 });
            if (type === 'company' && companyID) {
                Cookies.set('numberBlocksExportCompanyID', companyID, {
                    expires: 7,
                });
            } else {
                Cookies.remove('numberBlocksExportCompanyID');
            }

            setTemplateLoading(false);
        } catch (e) {
            setError(
                e?.message || 'Error downloading template. Please try again.',
            );
            console.error('Error downloading template:', e);
            setTemplateLoading(false);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const dataIsUnchangedFilter = (originalData, uploadedData) => {
        const fieldsToCompare = ['companyID'];
        const original = pick(originalData, fieldsToCompare);
        const uploaded = pick(uploadedData, fieldsToCompare);

        // Handle empty strings and null as equivalent for companyID
        if (original.companyID === null || original.companyID === '')
            original.companyID = '';
        if (uploaded.companyID === null || uploaded.companyID === '')
            uploaded.companyID = '';

        return isEqual(original, uploaded);
    };

    const handleUploadData = async (e) => {
        try {
            const data = e.target.files[0];
            if (!data) {
                return;
            }
            if (data.type !== CSV_TYPE) {
                setError(CSV_TYPE_ERROR);
                return;
            }

            setError(null);
            setUploadData(data);

            // Reset verification checks
            setVerificationChecks(
                VERIFICATION_CHECKS.map((check, idx) => ({
                    ...check,
                    check: false,
                    current: idx === 0,
                    completionCounter: 0,
                })),
            );

            // Step 1: Received CSV
            changeVerificationChecks(0);

            // Step 2: Fetch fresh blocks for validation
            let url;
            if (currentScope === 40) {
                url = `/partner/${currentPartner}/numberblocks/list`;
            } else if (currentScope === 60) {
                url = `/numberblocks/list/${currentWholesaler}`;
            } else if (currentScope === 80) {
                url = '/numberblocks/list';
            }

            const blocksResponse = await axios.get(url);
            const allBlocks = blocksResponse.data || [];

            const newLookup = {
                numberBlocksLookup: keyBy(allBlocks, 'id'),
            };

            setLookup(newLookup);
            changeVerificationChecks(1);

            // Step 3: Parse CSV
            Papa.parse(data, {
                header: true,
                skipEmptyLines: true,
                complete: async (results) => {
                    changeVerificationProgresses(2);

                    const parsedData = results.data.map((row) => {
                        // Map CSV headers back to internal field names
                        const reversedFieldsMap = Object.entries(
                            FIELDS_TO_EXPORT,
                        ).reduce((acc, [key, value]) => {
                            acc[value] = key;
                            return acc;
                        }, {});

                        const mappedRow = {};
                        Object.keys(row).forEach((csvKey) => {
                            const internalKey = reversedFieldsMap[csvKey];
                            if (internalKey) {
                                let value = row[csvKey];

                                // Handle boolean fields
                                if (
                                    internalKey === 'publicPool' ||
                                    internalKey === 'delete'
                                ) {
                                    const upperValue = String(value)
                                        .trim()
                                        .toUpperCase();
                                    value =
                                        upperValue === 'TRUE' ||
                                        upperValue === '1' ||
                                        upperValue === 'YES';
                                }

                                // Trim string values
                                if (typeof value === 'string') {
                                    value = value.trim();

                                    // Handle FALSE as empty for Company ID field
                                    if (
                                        internalKey === 'companyID' &&
                                        value.toUpperCase() === 'FALSE'
                                    ) {
                                        value = '';
                                    }
                                }

                                mappedRow[internalKey] = value;
                            }
                        });

                        return mappedRow;
                    });

                    changeVerificationProgresses(2);
                    changeVerificationChecks(2);

                    // Step 4: Validate number blocks with the lookup we just created
                    await validateNumberBlocks(parsedData, newLookup);
                },
                error: (error) => {
                    console.error('CSV parsing error:', error);
                    setError('Failed to parse CSV file');
                },
            });
        } catch (e) {
            console.error('Upload error:', e);
            setError(e?.message || 'Error uploading file. Please try again.');
        }
    };

    const validateNumberBlocks = async (parsedData, lookupData) => {
        try {
            const passed = [];
            const failed = [];

            // Update per-block step totals to match actual number of blocks
            setVerificationChecks((prev) =>
                prev.map((check, idx) =>
                    idx === 3 || idx === 4 || idx === 5
                        ? { ...check, total: parsedData.length }
                        : check,
                ),
            );

            // Read download context from cookies
            const exportType = Cookies.get('numberBlocksExportType');
            const exportCompanyID = Cookies.get('numberBlocksExportCompanyID');

            for (const block of parsedData) {
                const errors = [];
                const blockID = block.id;

                // Check if block exists - if not, skip update
                const originalBlock = lookupData.numberBlocksLookup[blockID];

                if (!originalBlock) {
                    errors.push(BLOCK_DNE_ERROR);
                    failed.push({
                        ...block,
                        errors,
                    });
                    continue;
                }

                changeVerificationProgresses(3);

                const csvDelete = Boolean(block.delete);

                // Check if read-only fields were modified (skip for deletions)
                // Strip any leading apostrophe that sanitizeCSVField may have added
                // to values starting with +/-/= to prevent CSV injection
                const stripSanitizer = (val) => {
                    const s = (val ?? '').toString().trim();
                    return s.startsWith("'") ? s.slice(1) : s;
                };

                if (!csvDelete) {
                    const csvFirst = stripSanitizer(block.first);
                    const csvLast = stripSanitizer(block.last);
                    const csvBlockSize = stripSanitizer(block.blockSize);
                    const originalFirst = stripSanitizer(originalBlock.first);
                    const originalLast = stripSanitizer(originalBlock.last);
                    const originalBlockSize = stripSanitizer(
                        originalBlock.blockSize,
                    );

                    if (
                        csvFirst !== originalFirst ||
                        csvLast !== originalLast ||
                        csvBlockSize !== originalBlockSize
                    ) {
                        errors.push(READ_ONLY_FIELD_MODIFIED_ERROR);
                    }
                }

                // Enrich with original data
                block.first = originalBlock.first;
                block.last = originalBlock.last;
                block.blockSize = originalBlock.blockSize;
                block.partnerName = originalBlock.partnerName || '';
                block.inboundCarrierName = originalBlock.inboundCarrier || '';
                block.outboundCarrierName = originalBlock.outboundCarrier || '';

                changeVerificationProgresses(4);

                // Determine original company ID based on export context from cookies
                let originalCompanyID;
                if (exportType === 'company' && exportCompanyID) {
                    // Downloaded company-specific blocks - original should be that company
                    originalCompanyID = exportCompanyID;
                } else if (exportType === 'unassigned') {
                    // Downloaded unassigned blocks - original should be empty
                    originalCompanyID = '';
                } else {
                    // Fallback: use current DB state (fresh lookup)
                    originalCompanyID = originalBlock.companyID || '';
                }

                // Normalize for comparison
                originalCompanyID = (originalCompanyID || '').toString().trim();
                const csvCompanyID = (block.companyID || '').toString().trim();

                // Debug first 3 blocks
                if (parsedData.indexOf(block) < 3) {
                    console.log(`\nBlock ${block.id}:`);
                    console.log('  Export Type:', exportType);
                    console.log('  Export Company ID:', exportCompanyID);
                    console.log(
                        '  Determined Original Company ID:',
                        originalCompanyID,
                    );
                    console.log('  CSV Company ID:', csvCompanyID);
                    console.log(
                        '  Current DB Company ID:',
                        originalBlock.companyID,
                    );
                    console.log(
                        '  Changed?',
                        originalCompanyID !== csvCompanyID,
                    );
                    console.log('  Delete?', csvDelete);
                }

                const companyChanged = originalCompanyID !== csvCompanyID;
                const carrierChanged =
                    (originalBlock.inboundCarrier || '').toString().trim() !==
                        (block.inboundCarrier || '').toString().trim() ||
                    (originalBlock.outboundCarrier || '').toString().trim() !==
                        (block.outboundCarrier || '').toString().trim();
                const partnerChanged =
                    (originalBlock.partnerID || '').toString().trim() !==
                    (block.partnerID || '').toString().trim();
                const poolChanged =
                    Boolean(originalBlock.publicPool) !==
                    Boolean(block.publicPool);

                const anyChange =
                    companyChanged ||
                    carrierChanged ||
                    partnerChanged ||
                    poolChanged;

                // Validation for deletion:
                // If company name exists (block is assigned), don't allow delete
                // If company name doesn't exist (unassigned) and delete is true, allow delete (pass)
                if (csvDelete) {
                    const hasCompany =
                        originalBlock.companyName &&
                        originalBlock.companyName.trim() !== '';
                    if (hasCompany) {
                        errors.push(CANNOT_DELETE_WITH_COMPANY_ERROR);
                    }
                    // If no company and delete is true, it's valid - no error added
                }
                // Must have at least one change if not deleting
                else if (!anyChange) {
                    errors.push(NO_CHANGES_ERROR);
                }

                changeVerificationProgresses(5);

                block.errors = errors;

                if (errors.length > 0) {
                    failed.push(block);
                } else {
                    passed.push(block);
                }
            }

            changeVerificationChecks(3);
            changeVerificationChecks(4);
            changeVerificationChecks(5);

            // Prepare payloads for API calls
            const apiPayloads = passed.map((block) => ({
                id: block.id,
                companyID: block.companyID || null, // NULL means remove from company
                delete: Boolean(block.delete), // Include delete flag for deletion
                partnerID: block.partnerID,
                inboundCarrierID: block.inboundCarrier,
                outboundCarrierID: block.outboundCarrier,
                publicPool: block.publicPool,
            }));

            setPayloads(apiPayloads);
            setVerificationResult({
                passed: {
                    columns: [],
                    data: passed,
                },
                failed: {
                    columns: [],
                    data: failed,
                },
            });
        } catch (e) {
            console.error('Validation error:', e);
            setError(
                e?.message ||
                    'Error validating number blocks. Please try again.',
            );
        }
    };

    return {
        handleDownloadCSVTemplate,
        handleUploadData,
        templateLoading,
        verificationChecks,
        uploadData,
        handleExportInvalidData,
        verificationResult,
        payloads,
        error,
        exportType,
        setExportType,
        selectedCompanyForExport,
        setSelectedCompanyForExport,
    };
}
